import { CanvasTexture, LinearFilter } from "three";
import { fetchImage, isMobile, resizeImage } from "../utilities";

interface CachedResponse {
  texture: CanvasTexture;
  missing: boolean;
  cancelled: boolean;
  lastAccess: number;
  path: string;
  width: number;
  height: number;
}

export class TextureCache {
  private cache : { [path: string]: CachedResponse } = {};
  private maxCacheSize = 0;
  private maxTextureSize = Infinity;
  private maxDeviceSize = isMobile ? 4096 : 8192
  private isThumbnailCache: boolean;

  constructor(maxCacheSize: number, isThumbnailCache: boolean) {
    this.maxCacheSize = maxCacheSize;
    this.isThumbnailCache = isThumbnailCache;
  }

  setMaxTextureSize(textureSize: number) {
    this.maxTextureSize = Math.min(this.maxDeviceSize, textureSize);
  }

  async get(imagePath: string, controller: AbortController): Promise<CachedResponse> {
    this.manage();

    if (this.isThumbnailCache) {
      imagePath = `${imagePath}?thumbnail=true`;
    }

    if (imagePath in this.cache) {
      // Update last used timestamp and return cached values
      this.cache[imagePath].lastAccess = performance.now();
      return this.cache[imagePath];
    }

    const response = await this.load(imagePath, controller);

    // Textures are not cached for mobile devices
    if (isMobile) return response;

    this.cache[imagePath] = response;
    return this.cache[imagePath];
  }

  reset() {
    const keys = Object.keys(this.cache);
    const toRemove = keys.map(key => this.cache[key]);
    this.closeImages(toRemove);

    // Reset cache values
    this.cache = {};
  }

  /** @private */
  closeImages(images: CachedResponse[]) {
    // Close all image bitmaps to free up memory
    images.forEach(imageData => {
      const {texture} = imageData;
      if (!texture) return;

      const image = texture.image;

      if (image instanceof ImageBitmap) {
        image.close();
      } else if (image instanceof HTMLCanvasElement) {
        image.remove();
      }
    });
  }

  /** @private */
  async load(path: string, controller: AbortController): Promise<CachedResponse> {
    let image = null;
    let texture = null;
    let missing = false;
    let cancelled = false;
    let height = null;
    let width = null;

    try {
      const imageInfo = await this.getImageInfo(path, controller);

      image = imageInfo.image;
      texture = new CanvasTexture(image);
      texture.minFilter = LinearFilter;
      texture.generateMipmaps = false;
      width = imageInfo.width;
      height = imageInfo.height;
    } catch (err) {
      // Request was aborted on purpose
      cancelled = err.code === DOMException.ABORT_ERR;

      // Image must be missing
      missing = true;
    }

    const lastAccess = performance.now();

    return {
      texture,
      missing,
      cancelled,
      lastAccess,
      path,
      width,
      height
    };
  }

  /** @private */
  manage() {
    const keys = Object.keys(this.cache);
    let data = keys.map(key => this.cache[key]);

    // Remove cancelled requests
    data = data.filter(imageData => !imageData.cancelled);

    // Remove oldest requests
    data.sort((a,b) => a.lastAccess - b.lastAccess);
    data.reverse();

    const toKeep = data.slice(0, this.maxCacheSize);
    const toRemove = data.slice(this.maxCacheSize);

    this.closeImages(toRemove);

    // Rebuild final cache
    this.cache = {};
    toKeep.forEach(imageData => {
      const key = imageData.path;
      this.cache[key] = imageData;
    });
  }

  /** @private */
  async getImageInfo(path: string, controller: AbortController) {
    const signal = controller.signal;
    const image = await fetchImage(path, signal);
    const originalWidth = image.width;
    const originalHeight = image.height;

    const maxEdgeLength = Math.max(image.width, image.height);
    const needsResize = maxEdgeLength > this.maxTextureSize;

    if (needsResize) {
      const scale = this.maxTextureSize/maxEdgeLength;
      const width = Math.floor(image.width * scale);
      const height = Math.floor(image.height * scale);
      const resized = await resizeImage(image, width, height);
      return {image: resized, width: originalWidth, height: originalHeight};
    }

    return {image: image, width: originalWidth, height: originalHeight};
  }
}
